import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Spinner } from '@/components/ui';
import ResponsiveModal from '../../../components/ResponsiveModal';
import GoogleGPSMap from '../../../components/GoogleGPSMap';
import { assetService } from '../../../services/assetService';
import { workIdService, type WorkIdFilterOptions } from '../../../services/workIdService';
import {
  bulkLinkWorkId,
  patchPcrSubmissionStatus,
  type PcrStatus,
} from '../../../services/pcrService';
import type { Asset } from '../../../types/Asset';
import { ASSET_TYPES, ASSET_TYPE_LABELS } from '../../../constants/assetTypes';
import type { ToastType } from '../../../contexts/ToastContext';

export interface PcrBulkCreateWorkIdModalProps {
  visible: boolean;
  selectedIds: number[];
  onClose: () => void;
  onSuccess: (
    message: string,
    toastType?: ToastType,
    options?: { clearSelection?: boolean },
  ) => void;
}

type Step = 'form' | 'statusPrompt';

const PcrBulkCreateWorkIdModal: React.FC<PcrBulkCreateWorkIdModalProps> = ({
  visible,
  selectedIds,
  onClose,
  onSuccess,
}) => {
  const count = selectedIds.length;

  // Step state
  const [step, setStep] = useState<Step>('form');
  const [createdWorkIdId, setCreatedWorkIdId] = useState<number | null>(null);
  const [createdWorkIdentificationId, setCreatedWorkIdentificationId] = useState<string | null>(null);

  // Filter options
  const [filterOptions, setFilterOptions] = useState<WorkIdFilterOptions | null>(null);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterError, setFilterError] = useState<string | null>(null);

  // Form fields
  const [foremanAreaId, setForemanAreaId] = useState<number | ''>('');
  const [priority, setPriority] = useState('');
  const [comments, setComments] = useState('');
  const [assetType, setAssetType] = useState<string>(ASSET_TYPES.ROAD);
  const [assetId, setAssetId] = useState<number | ''>('');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [showMap, setShowMap] = useState(true);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Status prompt
  const [applyingStatus, setApplyingStatus] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const selectedForeman = filterOptions?.foremanAreas.find((fa) => fa.id === foremanAreaId);

  // Load filter options when modal opens
  useEffect(() => {
    if (!visible) {
      // Reset form on close
      setStep('form');
      setCreatedWorkIdId(null);
      setCreatedWorkIdentificationId(null);
      setForemanAreaId('');
      setPriority('');
      setComments('');
      setAssetType(ASSET_TYPES.ROAD);
      setAssetId('');
      setAssets([]);
      setLocation(null);
      setShowMap(true);
      setError(null);
      setFilterError(null);
      setStatusError(null);
      return;
    }
    let cancelled = false;
    setFilterLoading(true);
    setFilterError(null);
    workIdService
      .getFilterOptions()
      .then((opts) => {
        if (!cancelled) setFilterOptions(opts);
      })
      .catch((e) => {
        if (!cancelled)
          setFilterError(e instanceof Error ? e.message : 'Failed to load filter options');
      })
      .finally(() => {
        if (!cancelled) setFilterLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visible]);

  // Load assets when foreman area or type changes
  useEffect(() => {
    if (!foremanAreaId || !visible) {
      setAssets([]);
      return;
    }
    let cancelled = false;
    setAssetsLoading(true);
    assetService
      .searchAssets({ foremanAreaId: Number(foremanAreaId), assetType })
      .then((data) => {
        if (!cancelled) setAssets(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setAssets([]);
      })
      .finally(() => {
        if (!cancelled) setAssetsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, foremanAreaId, assetType]);

  const isFormValid =
    foremanAreaId !== '' &&
    Number(foremanAreaId) > 0 &&
    priority.trim().length > 0 &&
    comments.trim().length > 0 &&
    assetId !== '' &&
    Number(assetId) > 0 &&
    !!location;

  const handleCreate = async () => {
    if (!isFormValid || !location) return;
    setSubmitting(true);
    setError(null);
    try {
      const workId = await workIdService.create({
        foremanAreaId: Number(foremanAreaId),
        comments: comments.trim(),
        priority,
        assetId: Number(assetId),
        latitude: location.lat,
        longitude: location.lng,
      });
      const linkResult = await bulkLinkWorkId(selectedIds, workId.id);
      const widLabel = workId.workIdentificationId ?? String(workId.id);
      if (linkResult.successCount < selectedIds.length) {
        const errText = linkResult.errors.slice(0, 5).join(' ');
        const more = linkResult.errors.length > 5 ? ' …' : '';
        if (linkResult.successCount === 0) {
          setError(
            `Work ID ${widLabel} was created, but no PCRs could be linked.${errText ? ` ${errText}${more}` : ''}`,
          );
        } else {
          setError(
            `Work ID ${widLabel} was created, but only ${linkResult.successCount} of ${selectedIds.length} PCRs linked.${errText ? ` ${errText}${more}` : ''}`,
          );
        }
        return;
      }
      setCreatedWorkIdId(workId.id);
      setCreatedWorkIdentificationId(workId.workIdentificationId ?? null);
      setStep('statusPrompt');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create Work ID');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChoice = async (status: PcrStatus | null) => {
    const workLabel = createdWorkIdentificationId ?? `ID ${createdWorkIdId}`;
    if (status === 'internal_review') {
      setApplyingStatus(true);
      setStatusError(null);
      try {
        await Promise.all(
          selectedIds.map((id) => patchPcrSubmissionStatus(String(id), { status: 'internal_review' })),
        );
      } catch (e) {
        setStatusError(e instanceof Error ? e.message : 'Failed to update status');
        setApplyingStatus(false);
        return;
      }
      setApplyingStatus(false);
      onSuccess(
        `Work ID ${workLabel} created. ${count} PCR${count !== 1 ? 's' : ''} linked and moved to Internal Review.`,
      );
    } else {
      onSuccess(
        `Work ID ${workLabel} created. ${count} PCR${count !== 1 ? 's' : ''} linked and finalized.`,
      );
    }
    onClose();
  };

  if (!visible) return null;

  if (step === 'statusPrompt') {
    return (
      // `autoHeight`: two buttons and a couple of lines of text don't need the standard
      // fixed-height treatment — without it, `lg` pins to 85vh on desktop and renders as a
      // mostly-empty sheet (the "work ID modals have a lot of whitespace" report).
      <Modal
        show
        onHide={() => {
          onSuccess(
            `Work ID ${createdWorkIdentificationId ?? `ID ${createdWorkIdId}`} created. ${count} PCR${count !== 1 ? 's' : ''} linked.`,
          );
          onClose();
        }}
        size="lg"
        autoHeight
      >
        <Modal.Header closeButton>
          <Modal.Title>Work ID created – set PCR status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="tw-text-center tw-py-2">
            <div className="tw-mb-3">
              <i
                className="bi bi-check-circle-fill tw-text-success"
                style={{ fontSize: '2rem' }}
                aria-hidden
              />
            </div>
            <p className="tw-mb-1 tw-font-semibold">
              Work ID{' '}
              <span className="tw-text-primary">
                {createdWorkIdentificationId ?? `#${createdWorkIdId}`}
              </span>{' '}
              created
            </p>
            <p className="tw-text-content-muted tw-text-sm tw-mb-4">
              {count} PCR{count !== 1 ? 's' : ''} linked. How should the PCRs be classified?
            </p>

            {statusError && <Alert variant="danger" className="tw-mb-3 tw-text-sm">{statusError}</Alert>}

            <div className="tw-flex tw-justify-center tw-gap-3 tw-flex-wrap">
              <Button
                variant="outline-primary"
                disabled={applyingStatus}
                onClick={() => void handleStatusChoice('internal_review')}
                style={{ minWidth: 160 }}
              >
                {applyingStatus ? (
                  <>
                    <Spinner size="sm" animation="border" className="tw-mr-2" />
                    Applying…
                  </>
                ) : (
                  <>
                    <i className="bi bi-arrow-right-circle tw-mr-2" aria-hidden />
                    Internal Review
                  </>
                )}
              </Button>
              <Button
                variant="success"
                disabled={applyingStatus}
                onClick={() => void handleStatusChoice(null)}
                style={{ minWidth: 160 }}
              >
                <i className="bi bi-check2-all tw-mr-2" aria-hidden />
                Keep as Finalized
              </Button>
            </div>
            <p className="tw-text-content-muted tw-text-sm tw-mt-4 tw-mb-0 tw-px-2">
              The Work ID&apos;s creation timestamp is stored for WRAP internal reporting only – not as a contractual
              service-area response-time clock.
            </p>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <ResponsiveModal
      isOpen
      onClose={() => !submitting && onClose()}
      title={`Create Work ID for ${count} PCR${count !== 1 ? 's' : ''}`}
      size="large"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleCreate()}
            disabled={!isFormValid || submitting || filterLoading}
          >
            {submitting ? (
              <>
                <Spinner size="sm" animation="border" className="tw-mr-2" />
                Creating…
              </>
            ) : (
              'Create Work ID'
            )}
          </Button>
        </>
      }
    >
      <p className="tw-text-content-muted tw-text-sm tw-mb-3">
        A single Work ID will be created and all {count} selected PCR
        {count !== 1 ? 's' : ''} will be linked to it. You will then be asked how to classify the
        PCRs.
      </p>

      {filterLoading && (
        <div className="tw-text-center tw-py-3">
          <Spinner animation="border" size="sm" />
          <span className="tw-ml-2 tw-text-content-muted tw-text-sm">Loading options…</span>
        </div>
      )}
      {filterError && <Alert variant="danger" className="tw-text-sm">{filterError}</Alert>}
      {error && <Alert variant="danger" className="tw-text-sm">{error}</Alert>}

      {!filterLoading && !filterError && filterOptions && (
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            void handleCreate();
          }}
        >
          {/* Location section */}
          <div className="tw-mb-4">
            <strong className="tw-block tw-mb-3">Assignment</strong>
            <Row>
              <Col xs={12} md={6}>
                <Form.Group controlId="bulk-wid-foreman">
                  <Form.Label>
                    Foreman area <span className="tw-text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={foremanAreaId === '' ? '' : String(foremanAreaId)}
                    onChange={(e) => {
                      setForemanAreaId(e.target.value ? Number(e.target.value) : '');
                      setAssetId('');
                    }}
                  >
                    <option value="">Select foreman area</option>
                    {filterOptions.foremanAreas.map((fa) => (
                      <option key={fa.id} value={fa.id}>
                        {fa.name}
                        {fa.serviceAreaName ? ` – ${fa.serviceAreaName}` : ''}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group controlId="bulk-wid-priority">
                  <Form.Label>
                    Priority <span className="tw-text-danger">*</span>
                  </Form.Label>
                  <Form.Select value={priority} onChange={(e) => setPriority(e.target.value)}>
                    <option value="">Select priority</option>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* Description */}
          <div className="tw-mb-4">
            <strong className="tw-block tw-mb-3">Description</strong>
            <Form.Group controlId="bulk-wid-comments">
              <Form.Label>
                Work description <span className="tw-text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Describe the work to be done…"
              />
            </Form.Group>
          </div>

          {/* Asset */}
          <div className="tw-mb-4">
            <strong className="tw-block tw-mb-3">Asset</strong>
            <Row>
              <Col xs={12} md={6}>
                <Form.Group controlId="bulk-wid-asset-type">
                  <Form.Label>Asset type</Form.Label>
                  <Form.Select
                    value={assetType}
                    onChange={(e) => {
                      setAssetType(e.target.value);
                      setAssetId('');
                    }}
                  >
                    {(Object.values(ASSET_TYPES) as string[]).map((type) => (
                      <option key={type} value={type}>
                        {ASSET_TYPE_LABELS[type as keyof typeof ASSET_TYPE_LABELS] ?? type}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group controlId="bulk-wid-asset">
                  <Form.Label>
                    Asset <span className="tw-text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={assetId === '' ? '' : String(assetId)}
                    onChange={(e) => setAssetId(e.target.value ? Number(e.target.value) : '')}
                    disabled={!foremanAreaId || assetsLoading}
                  >
                    <option value="">
                      {!foremanAreaId
                        ? 'Select a foreman area first'
                        : assetsLoading
                          ? 'Loading assets…'
                          : 'Select asset'}
                    </option>
                    {foremanAreaId &&
                      !assetsLoading &&
                      assets.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name} (#{a.assetNumber})
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* GPS */}
          <div className="tw-mb-2">
            <strong className="tw-block tw-mb-2">
              Location / GPS <span className="tw-text-danger">*</span>
            </strong>
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
              <p className="tw-text-content-muted tw-text-sm tw-mb-0">
                Click the map to place a pin for the Work ID location.
                {location ? (
                  <span className="tw-ml-2">
                    Lat {location.lat.toFixed(5)}, Lng {location.lng.toFixed(5)}
                  </span>
                ) : null}
              </p>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowMap((prev) => !prev)}
              >
                {showMap ? 'Hide map' : 'Show map'}
              </Button>
            </div>
            {showMap && (
              <GoogleGPSMap
                startLocation={location ?? undefined}
                endLocation={location ?? undefined}
                onLocationSelect={(lat, lng) => setLocation({ lat, lng })}
                locationType="start"
                isPointActivity
                autoFitBounds={false}
                height={260}
              />
            )}
            {!location && (
              <div className="tw-text-danger tw-text-sm tw-mt-1">Set a location on the map to continue.</div>
            )}
          </div>

          {selectedForeman && (
            <p className="tw-text-content-muted tw-text-sm tw-mt-2 tw-mb-0">
              Service area: <strong>{selectedForeman.serviceAreaName ?? '–'}</strong>
            </p>
          )}
        </Form>
      )}
    </ResponsiveModal>
  );
};

export default PcrBulkCreateWorkIdModal;
